import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@sbd-doug-frontend/app/environments/environment';
import { Observable, finalize, map } from 'rxjs';
import { SharedBaseActivityViewerService } from '@sbd-doug-frontend/shared/lib/shared-services/shared-base-activity-viewer.service';
import {
  ActivityViewerActor,
  ActivityViewerInstanceState,
  ActivityViewerItem,
  ActivityViewerQuoteRequest,
} from '@sbd-doug-frontend/shared/lib/types/activity-viewer/activity-viewer.types';
import {
  ActivityActor,
  QuoteRequestEventLog,
  RelatedQuoteRequestRef,
} from '@sbd-doug-frontend/shared/lib/types/activity-viewer/activity-event-log.types';

interface AccountActivityResponse {
  success: boolean;
  message: string;
  error: string | null;
  data: {
    account: {
      activity: QuoteRequestEventLog[];
      total_activities?: number;
    };
  };
}

interface CreateAccountActivityResponse {
  success: boolean;
  message: string;
  error: string | null;
  data?: {
    account?: {
      activity?: QuoteRequestEventLog | QuoteRequestEventLog[];
      activities?: QuoteRequestEventLog[];
    };
    activity?: QuoteRequestEventLog;
    activities?: QuoteRequestEventLog[];
  };
}

@Injectable({
  providedIn: 'root',
})
export class AccountManagerActivityService extends SharedBaseActivityViewerService {
  private _httpClient = inject(HttpClient);
  private readonly _storageKey = 'account-manager-activity-viewer-state-v1';
  private _instanceState: Record<string, ActivityViewerInstanceState> = {};
  private _storageListenerAttached = false;
  private _currentPageParams = {
    limit: 50,
    offset: 0,
  };

  initializeInstance(
    instanceId: string,
    scopeKey: string,
    options?: {
      initialActivityId?: string | number | null;
      drawerCollapsed?: boolean;
    }
  ): void {
    this._hydrateState();
    this._attachStorageListener();
    this.activeInstanceId.set(instanceId);

    const existing = this._instanceState[instanceId];
    this._instanceState[instanceId] = {
      instanceId,
      scopeKey,
      selectedActivityId: existing?.selectedActivityId ?? null,
      drawerCollapsed: options?.drawerCollapsed ?? existing?.drawerCollapsed ?? false,
    };

    const preferredActivityId = this._normalizeId(options?.initialActivityId);
    const restoredActivityId = this._instanceState[instanceId].selectedActivityId;
    const targetActivityId = preferredActivityId ?? restoredActivityId;
    this.setDrawerCollapsed(this._instanceState[instanceId].drawerCollapsed);
    this._loadActivities(instanceId, scopeKey, targetActivityId, this._currentPageParams);
  }

  setSelectedActivityById(activityId: string | number | null): void {
    const normalizedId = this._normalizeId(activityId);
    const selected = normalizedId
      ? this.activityItems().find((item) => item.activityId === normalizedId) || null
      : null;
    this.selectedActivity.set(selected);
    this._updateActiveInstance((instance) => {
      instance.selectedActivityId = normalizedId;
    });
  }

  refreshActivities(options?: { limit?: number; offset?: number }): void {
    const activeInstance = this._getActiveInstance();
    if (!activeInstance) {
      return;
    }

    this._currentPageParams = {
      limit: options?.limit ?? this._currentPageParams.limit,
      offset: options?.offset ?? this._currentPageParams.offset,
    };
    this._loadActivities(
      activeInstance.instanceId,
      activeInstance.scopeKey,
      activeInstance.selectedActivityId,
      this._currentPageParams
    );
  }

  createActivity(accountId: number, payload: QuoteRequestEventLog): Observable<QuoteRequestEventLog | null> {
    this.isLoading.set(true);

    return this._httpClient
      .post<CreateAccountActivityResponse>(`${environment.apiBaseUrl}/api/v4/account/${accountId}/activity`, payload)
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
        }),
        map((response) => {
          if (!response?.success) {
            return null;
          }

          return this._extractCreatedActivityLog(response);
        })
      );
  }

  updateActivityRelatedRequests(
    accountId: number,
    activity: ActivityViewerItem,
    relatedRequests: RelatedQuoteRequestRef[]
  ): Observable<ActivityViewerItem | null> {
    const activityId = Number(activity.activityId);
    if (!Number.isFinite(activityId)) {
      return new Observable<ActivityViewerItem | null>((subscriber) => {
        subscriber.next(null);
        subscriber.complete();
      });
    }

    return this._httpClient
      .patch<CreateAccountActivityResponse>(
        `${environment.apiBaseUrl}/api/v4/account/${accountId}/activity/${activityId}/associate`,
        {
          event_log_info: {
            activity: {
              related_quote_requests: relatedRequests,
            },
          },
          quote_request_id: relatedRequests[0]?.quote_request_id ?? null,
        }
      )
      .pipe(
        map((response) => {
          if (!response?.success) {
            return null;
          }

          const updatedActivity = this._extractAssociatedActivityLog(response, activityId);
          const mappedActivity = updatedActivity
            ? this._mapActivityItem(updatedActivity)
            : this._mergeRelatedRequests(activity, relatedRequests);

          this._replaceActivityItem(mappedActivity);
          return mappedActivity;
        })
      );
  }

  setDrawerCollapsed(collapsed: boolean): void {
    this.drawerCollapsed.set(collapsed);
    this._updateActiveInstance((instance) => {
      instance.drawerCollapsed = collapsed;
    });
  }

  private _updateActiveInstance(updateFn: (instance: ActivityViewerInstanceState) => void): void {
    const instanceId = this.activeInstanceId();
    if (!instanceId || !this._instanceState[instanceId]) {
      return;
    }

    const updated = { ...this._instanceState[instanceId] };
    updateFn(updated);
    this._instanceState[instanceId] = updated;
    this._persistState();
  }

  private _getActiveInstance(): ActivityViewerInstanceState | null {
    const instanceId = this.activeInstanceId();
    if (!instanceId) {
      return null;
    }

    return this._instanceState[instanceId] ?? null;
  }

  private _loadActivities(
    instanceId: string,
    scopeKey: string,
    targetActivityId: string | null,
    pageParams: { limit: number; offset: number }
  ): void {
    const accountId = this._parseAccountId(scopeKey);
    if (!accountId) {
      this.activityItems.set([]);
      this.totalActivities.set(0);
      this.setSelectedActivityById(null);
      return;
    }

    this.isLoading.set(true);

    this._httpClient
      .get<AccountActivityResponse>(`${environment.apiBaseUrl}/api/v4/account/${accountId}/activity`, {
        params: {
          limit: pageParams.limit,
          offset: pageParams.offset,
        },
      })
      .subscribe({
        next: (response) => {
          this.isLoading.set(false);
          const activeInstanceId = this.activeInstanceId();
          if (!response?.success || activeInstanceId !== instanceId) {
            return;
          }

          const mapped = this._mapActivityItems(response.data?.account?.activity ?? []);
          this.activityItems.set(mapped);
          this.totalActivities.set(response.data?.account?.total_activities ?? mapped.length);
          this.setSelectedActivityById(targetActivityId);
        },
        error: () => {
          this.isLoading.set(false);
          if (this.activeInstanceId() !== instanceId) {
            return;
          }

          this.activityItems.set([]);
          this.totalActivities.set(0);
          this.setSelectedActivityById(null);
        },
      });
  }

  private _extractCreatedActivityLog(response: CreateAccountActivityResponse): QuoteRequestEventLog | null {
    const activity =
      response.data?.activity ??
      response.data?.activities ??
      response.data?.account?.activity ??
      response.data?.account?.activities;
    if (Array.isArray(activity)) {
      return activity[0] ?? null;
    }

    return activity ?? null;
  }

  private _extractAssociatedActivityLog(
    response: CreateAccountActivityResponse,
    activityId: number
  ): QuoteRequestEventLog | null {
    const activityLogs = [
      ...(response.data?.account?.activities ?? []),
      ...(response.data?.activities ?? []),
      ...this._toActivityLogArray(response.data?.account?.activity),
      ...this._toActivityLogArray(response.data?.activity),
    ];

    return (
      activityLogs.find((activityLog) => activityLog.event_log_id === activityId) ??
      activityLogs.find((activityLog) => !activityLog.is_alias) ??
      activityLogs[0] ??
      null
    );
  }

  private _toActivityLogArray(
    activity: QuoteRequestEventLog | QuoteRequestEventLog[] | undefined
  ): QuoteRequestEventLog[] {
    if (!activity) {
      return [];
    }

    return Array.isArray(activity) ? activity : [activity];
  }

  private _mapActivityItems(activityLogs: QuoteRequestEventLog[]): ActivityViewerItem[] {
    return [...activityLogs]
      .sort((a, b) => this._toTimestamp(b.event_log_time ?? b.ctime) - this._toTimestamp(a.event_log_time ?? a.ctime))
      .map((log) => this._mapActivityItem(log));
  }

  private _mapActivityItem(log: QuoteRequestEventLog): ActivityViewerItem {
    const activityId =
      this._normalizeId(log.event_log_id) ??
      this._normalizeId(log.quote_request_id) ??
      `activity-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const subject = this._normalizeSummary(log.event_log_comment);
    const notes = this._normalizeHtml(log.event_log_info?.activity?.notes);
    const subtitleParts = [this._eventTypeLabel(log.event_log_type_id)];
    const relatedQuoteRequests = this._extractRelatedQuoteRequests(log);
    const actors = this._extractActors(log);

    return {
      activityId,
      title: this._extractTitle(subject, activityId),
      subtitle: subtitleParts.filter(Boolean).join(' | '),
      occurredAt: this._formatOccurredAt(log.event_log_time ?? log.ctime),
      occurredAtRaw: log.event_log_time ?? log.ctime,
      summary: notes || this._normalizeHtml(log.event_log_comment) || subject,
      notesSnippet: this._extractNotesSnippet(log.event_log_info?.activity?.notes),
      eventTypeId: log.event_log_type_id,
      eventTypeIcon: this._eventTypeIcon(log.event_log_type_id),
      adminInitials: this._extractAdminInitials(log),
      adminName: this._extractAdminName(log),
      actors,
      relatedQuoteRequests,
    };
  }

  private _replaceActivityItem(activity: ActivityViewerItem): void {
    this.activityItems.set(
      this.activityItems().map((item) => (item.activityId === activity.activityId ? activity : item))
    );
    if (this.selectedActivity()?.activityId === activity.activityId) {
      this.selectedActivity.set(activity);
    }
  }

  private _mergeRelatedRequests(
    activity: ActivityViewerItem,
    relatedRequests: RelatedQuoteRequestRef[]
  ): ActivityViewerItem {
    return {
      ...activity,
      relatedQuoteRequests: relatedRequests.map((quoteRequest) => this._mapRelatedQuoteRequestRef(quoteRequest)),
    };
  }

  private _mapRelatedQuoteRequestRef(quoteRequest: RelatedQuoteRequestRef): ActivityViewerQuoteRequest {
    return {
      quoteRequestId: quoteRequest.quote_request_id,
      orderNumber: quoteRequest.order_number,
      label: quoteRequest.label,
      userId: quoteRequest.user_id,
      companyName: quoteRequest.company_name,
      bondAmount: quoteRequest.bond_amount,
      stateAbbreviation: quoteRequest.state_abbreviation,
      suretyBondLabel: quoteRequest.surety_bond_label,
    };
  }

  private _extractNotesSnippet(notes: string | null | undefined): string {
    const text = this._normalizeText(notes);
    if (!text) {
      return '';
    }

    return text.length > 140 ? `${text.slice(0, 140)}...` : text;
  }

  private _extractAdminInitials(log: QuoteRequestEventLog): string {
    const firstName = this._normalizeText(log.event_log_admin_user_first_name);
    const lastName = this._normalizeText(log.event_log_admin_user_last_name);
    const initials = [firstName, lastName]
      .map((name) => name.charAt(0))
      .join('')
      .toUpperCase();

    return initials || firstName.slice(0, 2).toUpperCase();
  }

  private _extractAdminName(log: QuoteRequestEventLog): string {
    return [log.event_log_admin_user_first_name, log.event_log_admin_user_last_name]
      .map((name) => this._normalizeText(name))
      .filter(Boolean)
      .join(' ');
  }

  private _extractActors(log: QuoteRequestEventLog): ActivityViewerActor[] {
    const actors = log.event_log_info?.activity?.actors ?? [];
    return actors.map((actor) => this._mapActor(actor)).filter((actor): actor is ActivityViewerActor => !!actor);
  }

  private _mapActor(actor: ActivityActor): ActivityViewerActor | null {
    const fullName = actor.actor_full_name?.trim();
    if (!fullName) {
      return null;
    }

    return {
      actorId: actor.actor_id,
      scopeKey: actor.actor_scope_key,
      fullName,
      initials: actor.actor_initials,
    };
  }

  private _extractRelatedQuoteRequests(log: QuoteRequestEventLog): ActivityViewerQuoteRequest[] {
    const extracted = new Map<number, ActivityViewerQuoteRequest>();

    if (log.quote_request_id) {
      extracted.set(log.quote_request_id, {
        quoteRequestId: log.quote_request_id,
        orderNumber: log.order_number,
      });
    }

    const related = log.event_log_info?.activity?.related_quote_requests ?? [];
    related.forEach((quoteRequest) => {
      if (!quoteRequest?.quote_request_id) {
        return;
      }

      extracted.set(quoteRequest.quote_request_id, {
        quoteRequestId: quoteRequest.quote_request_id,
        orderNumber: quoteRequest.order_number,
        label: quoteRequest.label,
        userId: quoteRequest.user_id,
        companyName: quoteRequest.company_name,
        bondAmount: quoteRequest.bond_amount,
        stateAbbreviation: quoteRequest.state_abbreviation,
        suretyBondLabel: quoteRequest.surety_bond_label,
      });
    });

    return Array.from(extracted.values());
  }

  private _extractTitle(summary: string, fallbackId: string): string {
    const firstLine = summary
      .split('\n')
      .map((line) => line.trim())
      .find((line) => line.length > 0);

    if (!firstLine) {
      return `Activity ${fallbackId}`;
    }

    return firstLine.length > 90 ? `${firstLine.slice(0, 90)}...` : firstLine;
  }

  private _normalizeSummary(comment: string | null | undefined): string {
    const normalized = this._normalizeText(comment);
    return normalized || 'No notes provided.';
  }

  private _normalizeHtml(value: string | null | undefined): string {
    const rawValue = value ?? '';
    const plainText = this._htmlToPlainText(rawValue).trim();
    return plainText ? rawValue.trim() : '';
  }

  private _normalizeText(value: string | null | undefined): string {
    return this._htmlToPlainText(value ?? '').trim();
  }

  private _htmlToPlainText(value: string): string {
    if (!value) {
      return '';
    }

    if (typeof window === 'undefined') {
      return value.replace(/<[^>]+>/g, ' ');
    }

    const parser = new DOMParser();
    const parsed = parser.parseFromString(value, 'text/html');
    const text = parsed.documentElement.textContent ?? '';
    return text.replace(/\u00a0/g, ' ');
  }

  private _eventTypeLabel(eventTypeId: number): string {
    const eventTypeMap: Record<number, string> = {
      30: 'Call In',
      31: 'Received Voicemail',
      32: 'Call Out',
      33: 'Left Voicemail',
      34: 'No Voicemail Available',
    };

    return eventTypeMap[eventTypeId] ?? `Type ${eventTypeId}`;
  }

  private _eventTypeIcon(eventTypeId: number): string {
    const incomingTypes = new Set([30, 31]);
    const outgoingTypes = new Set([32, 33, 34]);

    if (incomingTypes.has(eventTypeId)) {
      return 'heroicons_solid:phone-incoming';
    }

    if (outgoingTypes.has(eventTypeId)) {
      return 'heroicons_solid:phone-outgoing';
    }

    return 'heroicons_solid:phone';
  }

  private _formatOccurredAt(rawDate: string | null | undefined): string {
    if (!rawDate) {
      return '';
    }

    const timestamp = this._toTimestamp(rawDate);
    if (!timestamp) {
      return rawDate;
    }

    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(timestamp));
  }

  private _toTimestamp(rawDate: string | null | undefined): number {
    if (!rawDate) {
      return 0;
    }

    const timestamp = new Date(rawDate).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  private _parseAccountId(scopeKey: string): number | null {
    const match = scopeKey.match(/account:(\d+)/);
    if (!match) {
      return null;
    }

    const parsed = Number(match[1]);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private _normalizeId(value: string | number | null | undefined): string | null {
    if (value === null || value === undefined) {
      return null;
    }
    const normalized = String(value).trim();
    return normalized.length ? normalized : null;
  }

  private _hydrateState(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const saved = window.localStorage.getItem(this._storageKey);
      if (saved) {
        this._instanceState = JSON.parse(saved) as Record<string, ActivityViewerInstanceState>;
      }
    } catch {
      this._instanceState = {};
    }
  }

  private _persistState(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(this._storageKey, JSON.stringify(this._instanceState));
    } catch {
      // Ignore persistence failures in shell mode.
    }
  }

  private _attachStorageListener(): void {
    if (this._storageListenerAttached || typeof window === 'undefined') {
      return;
    }

    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key !== this._storageKey || !event.newValue) {
        return;
      }

      try {
        this._instanceState = JSON.parse(event.newValue) as Record<string, ActivityViewerInstanceState>;
      } catch {
        return;
      }

      const currentInstanceId = this.activeInstanceId();
      if (!currentInstanceId) {
        return;
      }

      const currentInstance = this._instanceState[currentInstanceId];
      if (!currentInstance) {
        return;
      }

      this.drawerCollapsed.set(currentInstance.drawerCollapsed);
      const selected = currentInstance.selectedActivityId
        ? this.activityItems().find((item) => item.activityId === currentInstance.selectedActivityId) || null
        : null;
      this.selectedActivity.set(selected);
    });

    this._storageListenerAttached = true;
  }
}
